// @flow
import styled, { css } from "styled-components";
import { animated } from "@react-spring/web";
import { Link } from "react-router-dom";
import { fontBold, fontNormal, fontCondensed } from "@fdr/static-ui/Typography";
import { generateTransition, cubic, linear } from "@fdr/static-ui/Transitions";
import buildColor from "@fdr/static-ui/ColorPalette";
import Icon from "@fdr/static-ui/Icons/";

import ArrowPagination from "../../_molecules/ArrowPagination";

export const Container = styled.div`
  z-index: 100;
  display: flex;
  flex-direction: column;
  ${({ isHeaderSelector }) =>
    isHeaderSelector
      ? css`
          align-items: flex-start;
          padding: 8px 12px 5px 12px;
        `
      : css`
          align-items: center;
          padding: 0 12px 12px;
        `};
  width: 100%;
  flex: 0 1 auto;
  min-width: 50px;
  padding: 0;
  background-color: ${buildColor("white", "default")};
`;

export const TitleContainer = styled.div`
  display: flex;
  position: sticky;
  top: ${({ withFixedBanner }) => (withFixedBanner ? "82px" : "48px")};
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding: 8px 16px;
  margin-bottom: 0;
  overflow: hidden;
  ${({ withFixedBanner, hasDerbyBanner }) =>
    (!hasDerbyBanner || withFixedBanner) &&
    css`
      border-bottom: 1px solid ${buildColor("grey", "L2")};
    `};
  background-color: ${({ isNightMode }) =>
    isNightMode ? buildColor("navy", "D2") : buildColor("white", "default")};
  z-index: 200;
`;

export const RaceTitleHeader = styled(animated.div)`
  width: 100%;
  height: 44px;
  display: ${({ show }) => (show ? "flex" : "none")};
  position: sticky;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  justify-content: center;
  margin-bottom: 8px;
  padding: 0 16px 18px;
  background-color: ${({ isNightMode }) =>
    isNightMode ? buildColor("navy", "D2") : buildColor("white", "default")};

  & > time {
    ${({ isResulted }) =>
      isResulted &&
      css`
        color: ${buildColor("white", "default")};
      `};
  }

  &:first-child {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0;
    margin-top: 0;
    padding: 16px;
  }

  &:nth-child(2) {
    margin-bottom: 0;
    padding-bottom: 16px;
    border-bottom: 1px solid ${buildColor("grey", "L2")};
  }

  &:last-child {
    > button {
      &:first-child:not(:last-child) {
        margin-right: 8px;
      }
    }
    & > a {
      padding: 0 16px;

      & > span {
        font-size: 16px;
        font-family: ${fontNormal};
      }
    }
  }

  a {
    height: 44px;
  }
`;

export const RaceSelectorWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;

  & a > span {
    position: absolute;
    margin-left: 32px;
    will-change: margin-top, opacity;
    transition: ${generateTransition(cubic, "margin-top")},
      ${generateTransition(linear, "opacity")};
    ${({ isNavigationShown }) =>
      isNavigationShown
        ? css`
            opacity: 0;
            margin-top: -40px;
          `
        : css`
            opacity: 1;
            margin-top: 0;
          `};
  }

  & > div {
    position: absolute;
    margin-left: 24px;
    width: auto;
    max-width: calc(100% - 178px);
    background-color: transparent;
    will-change: margin-top, opacity;
    transition: ${generateTransition(cubic, "margin-top")},
      ${generateTransition(linear, "opacity")};
    ${({ isNavigationShown }) =>
      isNavigationShown
        ? css`
            opacity: 1;
            margin-top: 0;
          `
        : css`
            opacity: 0;
            margin-top: 40px;
          `};
  }
`;

export const Navigation = styled(ArrowPagination)`
  margin-left: ${({ isNear }) => (isNear ? "8px" : "16px")};
`;

export const TrackDescription = styled.div`
  font-family: ${fontBold};
  font-weight: bold;
  letter-spacing: 0;
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
`;

export const Title = styled.h1`
  color: ${({ isNightMode }) =>
    isNightMode ? buildColor("white") : buildColor("navy")};
  font-size: 20px;
  margin-left: 8px;
  margin-right: 8px;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: normal;
`;

export const CountryTitle = styled.h3`
  display: flex;
  font-family: ${fontCondensed};
  color: ${({ isNightMode }) =>
    isNightMode ? buildColor("grey", "L2") : buildColor("grey", "D3")};
  font-size: 12px;
  font-weight: normal;
  align-self: flex-end;
  padding-bottom: 2px;
  letter-spacing: 1px;
`;

export const NavigationWrapper = styled.div`
  display: flex;
`;

export const SubTitle = styled.h2`
  color: ${buildColor("navy", "200")};
  font-family: ${fontNormal};
  font-size: 16px;
  font-weight: normal;
  letter-spacing: 0;
`;

export const WrapperTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
`;

export const IconWrapper = styled(Icon)`
  display: flex;
  flex: 0 0 16px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 44px;
  width: 100%;
  justify-content: center;
  flex: 1 1 auto;
  text-align: left;

  > a {
    display: flex;
    align-items: center;
    max-width: 230px;
  }
`;

const HeaderTextDefaultContainer = css`
  font-size: 16px;
  color: ${({ isNightMode }) =>
    isNightMode ? buildColor("white") : buildColor("navy", "default")};
  font-family: ${({ isSmall }) => (isSmall ? fontNormal : fontBold)};
  line-height: 125%;
`;

export const TrackNameContainer = styled.span`
  ${HeaderTextDefaultContainer};
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RaceNumberContainer = styled.span`
  ${HeaderTextDefaultContainer};
  padding-right: 2px;
  flex: 0;
`;

export const FloatButtonContainer = styled.div`
  position: absolute;
  right: 10px;

  & button {
    width: fit-content;
    padding: 0 12px;
  }
`;

export const LinkContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  position: absolute;
  z-index: 99;

  & > span {
    margin-left: 12px;
  }
`;
